import type { ColDef, Row } from "../domain/types";
import { quarterLabelToYYYYMM } from "../domain/period";
import { ScrollableX } from "../../components/ScrollableX";

export function FundReconciliationSection({
  selectedFunds,
  fundCols,
  periodsPerFund,
  selectedPeriod,
  renderCell,
}: {
  selectedFunds: Array<{ id?: number; fund_id?: number; fund_name?: string }>;
  fundCols: ColDef[];
  periodsPerFund: Map<number, string[]>;
  selectedPeriod: string | null;
  renderCell: (args: {
    entityType: "fund";
    entityId: number;
    fundId: number;
    reportingDate: number | null;
    row: Row;
    col: ColDef;
    fundName: string;
    companyName: string;
    disablePreviousComparison?: boolean;
  }) => React.ReactNode;
}) {
  if (selectedFunds.length === 0) return null;
  return (
    <div className="mt-10">
      <div className="mb-5 flex items-center gap-3">
        <h2 className="text-2xl font-serif text-slate-900">Fund overview</h2>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        <ScrollableX>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50/80">
                {fundCols.map((col) => (
                  <th
                    key={col.key}
                    className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-600"
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedFunds.map((fund) => {
                const fundId = Number(fund.id ?? fund.fund_id);
                const fundName = fund.fund_name || `Fund ${fundId}`;
                const selectedReportingDate =
                  quarterLabelToYYYYMM(selectedPeriod);
                const latestFundReportingDate = quarterLabelToYYYYMM(
                  (periodsPerFund.get(fundId) ?? [])[0] ?? null,
                );
                const fundReportingDate =
                  selectedReportingDate ?? latestFundReportingDate;
                return (
                  <tr key={fundId} className="border-t border-slate-100">
                    {fundCols.map((col) => (
                      <td
                        key={col.key}
                        className="px-3 py-2.5 transition-colors hover:bg-slate-100/70"
                      >
                        <div className="space-y-1">
                          {renderCell({
                            entityType: "fund",
                            entityId: fundId,
                            fundId,
                            reportingDate: fundReportingDate,
                            row: fund as unknown as Row,
                            col,
                            fundName,
                            companyName: fundName,
                            disablePreviousComparison: true,
                          })}
                        </div>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </ScrollableX>
      </div>
    </div>
  );
}
